import { put, call, takeEvery, delay, select } from 'redux-saga/effects';
import WebsocketService from '@upstart/harmony/lib/websocket-service';
import { EventType } from '@upstart/harmony/lib/types/websocketTypes';
import { getPencilWithTradeIns } from 'util/pencilUtils';
import { exponentialBackoff } from 'util/utils';
import { MAX_POLLING_BACKOFF } from 'util/constants';
import { getDeal } from 'redux/deals/selectors';
import {
  PENCIL_LIST_REQUEST,
  PENCIL_LIST_POLLING_RESET,
  REMOVE_EXPIRED_INCENTIVES_REQUEST,
  REMOVE_UNAVAILABLE_ACCESSORIES_REQUEST,
} from './actionTypes';
import {
  pencilListSuccess,
  pencilListFailure,
  pencilListPollingCancelledEvent,
} from './actions';
import { pencilSuccess } from '../deals/actions';
import PencilService from '../../services/PencilService';
import { pollingSaga } from '../util';

const VERIFY_HAS_NEW_PENCIL_RATE = 100;

export function* getPencilListTask(action) {
  const { dealId } = action.payload;
  let hasNewPencil = true;
  let failedAttempts = 0;

  try {
    // Make our subscription to the websocket event
    WebsocketService.subscribe(
      {
        dealId,
        eventType: EventType.DEAL_PENCIL_UPDATED,
      },
      async () => {
        // Change flag to true to indicate we have a new event
        hasNewPencil = true;
      },
    );

    while (true) {
      try {
        if (hasNewPencil) {
          const result = yield call(PencilService.getPencilList, dealId, null);
          yield put(pencilListSuccess(result.pencilList));
          hasNewPencil = false;
          failedAttempts = 0;
        }
      } catch (error) {
        failedAttempts += 1;
        yield put(pencilListFailure(error));
        // exponentially delay the next attempt up to max back off
        yield delay(
          exponentialBackoff(failedAttempts, 1000, MAX_POLLING_BACKOFF),
        );
      } finally {
        // Delay the loop so it checks for change in the hasNewPencil every VERIFY_HAS_NEW_PENCIL_RATE period
        yield delay(VERIFY_HAS_NEW_PENCIL_RATE);
      }
    }
  } finally {
    // This part is called when the saga is cancelled.
    // Be sure to unsubscribe from the websocket and also publish an event so that consumers
    // know when its OK to resubscribe. This is necessary to avoid race conditions - callers
    // who cancel this saga must know when the cancellation has finished so that they know
    // when they can safely set up new subscriptions.
    // This is necessary becasuse WebsocketService subscribe and unsubscribe are essentially
    // "memoized" - the below unsubscribe will remove _all_ previous subscriptions to {dealId, eventType}

    WebsocketService.unsubscribe({
      dealId,
      eventType: EventType.DEAL_PENCIL_UPDATED,
    });
    yield put(pencilListPollingCancelledEvent());
  }
}

export function* removeExpiredIncentives(action) {
  const { pencilId, callback, dealId, carId } = action.payload;
  const deal = yield select(getDeal);
  const result = yield call(
    PencilService.removeExpiredIncentives,
    pencilId,
    dealId,
    carId,
  );
  const pencil = getPencilWithTradeIns(
    yield call(PencilService.getPencil, result?.id),
    deal,
  );
  yield put(pencilSuccess(pencil));
  callback();
}

export function* removeUnavailableAccessories(action) {
  const { pencilId, callback, dealId, carId } = action.payload;
  const deal = yield select(getDeal);
  const result = yield call(
    PencilService.removeUnavailableAccessories,
    pencilId,
    dealId,
    carId,
  );
  const pencil = getPencilWithTradeIns(
    yield call(PencilService.getPencil, result?.id),
    deal,
  );
  yield put(pencilSuccess(pencil));
  callback();
}

export function* pencilsSaga() {
  yield takeEvery(
    PENCIL_LIST_REQUEST,
    pollingSaga,
    getPencilListTask,
    PENCIL_LIST_POLLING_RESET,
  );
  yield takeEvery(REMOVE_EXPIRED_INCENTIVES_REQUEST, removeExpiredIncentives);
  yield takeEvery(
    REMOVE_UNAVAILABLE_ACCESSORIES_REQUEST,
    removeUnavailableAccessories,
  );
}
